import React, { useEffect, useRef, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import axios from "axios"
import { AssetMap, HoleInOneConfirmation } from "../types"

type Stage = "select" | "uploading" | "preview"

interface ConfirmHoleInOneProps {
  confirmation: HoleInOneConfirmation
  roundId: number
  currentUserId: number
  editScores: () => void
  skip: () => void
  createHoleInOnePost: (payload: {
    message: string
    attachments: Array<{
      s3Url: string
      fileName: string
      fileSize: number
      mimeType: string
      mediaType: "image"
    }>
    roundId: number
    holeId: number
    teamRoundPlayerIds: number[]
  }) => Promise<boolean>
  assets: AssetMap
}

export default function ConfirmHoleInOne({
  confirmation,
  roundId,
  currentUserId,
  editScores,
  skip,
  createHoleInOnePost,
  assets
}: ConfirmHoleInOneProps) {
  const [stage, setStage] = useState<Stage>("select")
  const [files, setFiles] = useState<File[]>([])
  const [s3Urls, setS3Urls] = useState<string[]>([])
  const [fileProgresses, setFileProgresses] = useState<number[]>([])
  const [uploadsComplete, setUploadsComplete] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [message, setMessage] = useState("")
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const subject = confirmation.playerScores
    .map((playerScore) => (playerScore.userId === currentUserId ? "you" : playerScore.name))
    .join(" & ")

  useEffect(() => {
    setUploadsComplete(false)
    setS3Urls([])
    setFileProgresses([])

    if (files.length === 0) {
      return
    }

    const abortController = new AbortController()

    const startUploads = async () => {
      setStage("uploading")
      setFileError(null)
      setFileProgresses(Array(files.length).fill(0))

      try {
        const response = await axios.post(
          "/api/feed_posts/presigned_s3_url",
          { filename: files.map((file) => file.name) },
          { signal: abortController.signal }
        )
        const urls: string[] | undefined = response.data?.urls
        if (!urls || urls.length !== files.length) {
          throw new Error("Invalid presigned URL response")
        }
        if (abortController.signal.aborted) {
          return
        }

        setS3Urls(urls)

        await Promise.all(
          urls.map((url, index) =>
            axios.put(url, files[index], {
              headers: { "Content-Type": files[index].type },
              timeout: 120000,
              signal: abortController.signal,
              onUploadProgress: (event) => {
                if (!event.total || abortController.signal.aborted) {
                  return
                }
                setFileProgresses((prev) => {
                  const next = [...prev]
                  next[index] = event.loaded / event.total
                  return next
                })
              }
            })
          )
        )

        if (abortController.signal.aborted) {
          return
        }

        setUploadsComplete(true)
        setStage("preview")
      } catch (err) {
        if (abortController.signal.aborted || axios.isCancel(err) || err.code === "ERR_CANCELED") {
          return
        }
        console.error("Could not upload hole in one photos", err)
        setFileError("Could not upload photos. Please try again.")
        setFiles([])
        setS3Urls([])
        setFileProgresses([])
        setUploadsComplete(false)
        setStage("select")
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      }
    }

    void startUploads()

    return () => {
      abortController.abort()
    }
  }, [files])

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files
    if (!selected || selected.length < 1) {
      return
    }

    setFiles(Array.from(selected).slice(0, 10))
  }

  const submitPost = async () => {
    if (!uploadsComplete || s3Urls.length === 0 || saving) {
      return
    }

    setSaving(true)

    const attachments = files.map((file, index) => ({
      s3Url: baseS3Url(s3Urls[index]),
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      mediaType: "image" as const
    }))

    const success = await createHoleInOnePost({
      message,
      attachments,
      roundId,
      holeId: confirmation.holeId,
      teamRoundPlayerIds: confirmation.playerScores.map((playerScore) => playerScore.teamRoundPlayerId)
    })

    setSaving(false)
    if (success) {
      skip()
    }
  }

  const secondaryLinks = (
    <div className="secondary-links">
      <a onClick={editScores}>Edit Scores</a>
      <a onClick={skip}>Skip</a>
    </div>
  )

  if (stage === "uploading") {
    const filesUploaded = fileProgresses.filter((progress) => progress >= 1).length
    const totalProgress = fileProgresses.reduce((sum, progress) => sum + (progress || 0), 0)
    const processing = files.length > 0 && filesUploaded === files.length
    const percentComplete = files.length > 0 ? Math.round((totalProgress / files.length) * 100) : 0

    return (
      <div className="screen-body hole-in-one-photo">
        <div className="screen-heading">
          <h1 className="screen-title">{processing ? "Processing..." : "Uploading..."}</h1>
          <p>
            {processing
              ? "Finishing upload — this can take a moment on a slow connection."
              : `${filesUploaded} of ${files.length} photos uploaded`}
          </p>
        </div>
        <div className={`files-uploading ${processing ? "processing" : ""}`}>
          {processing ? (
            <div className="progress-track" aria-hidden="true">
              <div className="progress-bar indeterminate" />
            </div>
          ) : (
            <progress value={totalProgress} max={files.length || 1} />
          )}
          <div className="percent-complete">{processing ? "Processing..." : `${percentComplete}% complete`}</div>
        </div>
      </div>
    )
  }

  if (stage === "preview" && uploadsComplete && s3Urls.length > 0) {
    return (
      <div className="screen-body hole-in-one-photo">
        <div className="screen-heading">
          <h1 className="screen-title">Hole-In-One!</h1>
          <p>Looking good! Add a caption and post to the Spark feed.</p>
        </div>
        <div className="post-preview">
          {s3Urls.length === 1 ? (
            <div className="photo-preview">
              <ImageWithPlaceholder src={imgixPreviewUrl(s3Urls[0])} alt="Hole-in-one photo preview" />
            </div>
          ) : (
            <div className="photo-preview">
              <Swiper loop pagination>
                {s3Urls.map((s3Url, index) => (
                  <SwiperSlide key={index}>
                    <ImageWithPlaceholder src={imgixPreviewUrl(s3Url)} alt={`Hole-in-one photo ${index + 1}`} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
          <textarea
            name="message"
            placeholder="Write a caption..."
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={2200}
          />
        </div>
        <div style={{ marginTop: "2rem", width: "100%", textAlign: "center" }}>
          <a
            className={`action-button ${saving ? "disabled" : ""}`}
            style={{ width: "100%", maxWidth: "20rem" }}
            onClick={submitPost}
          >
            {saving ? (
              <img src={assets.buttonSpinner} style={{ paddingTop: "4px" }} alt="Posting..." />
            ) : (
              "Post to the Spark feed"
            )}
          </a>
        </div>
        <div className="secondary-links">
          <label htmlFor="hole-in-one-file-input">Choose different photos</label>
        </div>
        <input
          ref={fileInputRef}
          id="hole-in-one-file-input"
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelection}
        />
        {secondaryLinks}
      </div>
    )
  }

  return (
    <div className="screen-body hole-in-one-photo">
      <div className="screen-heading">
        <h1 className="screen-title">Hole-In-One!</h1>
        <p>
          Congrats! Looks like {subject} made a hole-in-one! Please take photos and post it to the Spark feed to
          celebrate!
        </p>
      </div>

      <div className="add-photo">
        <label htmlFor="hole-in-one-file-input">
          <div className="action-button">Add Photos</div>
        </label>
      </div>
      <input
        ref={fileInputRef}
        id="hole-in-one-file-input"
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelection}
      />

      {fileError ? (
        <div style={{ marginTop: "1rem", textAlign: "center", fontSize: "0.875rem", color: "#FB4D2F" }}>{fileError}</div>
      ) : null}

      {secondaryLinks}
    </div>
  )
}

function baseS3Url(s3Url: string) {
  const url = new URL(s3Url)
  return `${url.origin}${url.pathname}`
}

function imgixPreviewUrl(s3Url: string) {
  const contentWidth = Math.min(window.screen.availWidth, 688)
  const imageWidth = contentWidth * window.devicePixelRatio
  const url = new URL(s3Url)
  return `${url.origin.replace("s3.amazonaws.com", "imgix.net")}${url.pathname}?w=${imageWidth}&h=${imageWidth}&fit=crop&crop=faces`
}

function ImageWithPlaceholder({ src, alt }: { src: string; alt: string }) {
  const [retries, setRetries] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const size = Math.min(window.screen.availWidth, 688)

  const handleError = () => {
    if (retries < 3) {
      setTimeout(() => setRetries(retries + 1), 1000)
    }
  }

  const imgSrc = retries > 0 ? `${src}&retry=${retries}` : src

  return (
    <div className="image-holder">
      {loaded ? null : (
        <img
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mM88x8AAp0BzdNtlUkAAAAASUVORK5CYII="
          style={{ width: size, height: size }}
          width={size}
          height={size}
          alt=""
        />
      )}
      <img src={imgSrc} alt={alt} onLoad={() => setLoaded(true)} onError={handleError} />
    </div>
  )
}
